/**
 * WeekNavigator
 *
 * Navigation control for selecting which week to display in the performance table.
 * Shows inline chevron buttons and a calendar popover to jump to any available week.
 * The calendar uses range mode to highlight the full selected week and shows a
 * hover band when mousing over any day.
 */

import { useMemo, useState } from "react";
import { startOfWeek, addDays } from "date-fns";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/shared/components/ui/tooltip";
import type { DateRange } from "react-day-picker";
import { AvailableWeek } from "../../types/dashboard";

interface WeekNavigatorProps {
  weeks: AvailableWeek[];
  selectedIndex: number;
  onIndexChange: (index: number) => void;
}

function formatWeekLabel(week: AvailableWeek): string {
  const start = new Date(week.week_start + "T00:00:00");
  const end = addDays(start, 6); // full Mon–Sun week
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

export default function WeekNavigator({
  weeks,
  selectedIndex,
  onIndexChange,
}: WeekNavigatorProps) {
  const [open, setOpen] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const canGoNewer = selectedIndex > 0;
  const canGoOlder = selectedIndex < weeks.length - 1;
  const selectedWeek = weeks[selectedIndex];

  const weekStart = useMemo(
    () => new Date(selectedWeek.week_start + "T00:00:00"),
    [selectedWeek.week_start]
  );

  // Always show full Mon–Sun week regardless of how much data exists
  const weekEnd = useMemo(
    () => addDays(weekStart, 6),
    [weekStart]
  );

  const selectedRange: DateRange = useMemo(
    () => ({ from: weekStart, to: weekEnd }),
    [weekStart, weekEnd]
  );

  /** Array of 7 dates (Mon–Sun) representing the week the cursor is hovering over. */
  const hoveredWeekDays = useMemo(() => {
    if (!hoveredDay) return [];
    const start = startOfWeek(hoveredDay, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [hoveredDay]);

  /** Find the week containing (or closest to) the clicked calendar date. */
  const handleWeekSelect = (
    _selected: DateRange | undefined,
    triggerDate: Date
  ) => {
    const target = triggerDate.getTime();

    let bestIndex = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < weeks.length; i++) {
      const start = new Date(weeks[i].week_start + "T00:00:00").getTime();
      const end = new Date(weeks[i].week_end + "T00:00:00").getTime();

      if (target >= start && target <= end) {
        bestIndex = i;
        break;
      }

      const distance = Math.min(
        Math.abs(target - start),
        Math.abs(target - end)
      );
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    onIndexChange(bestIndex);
    setOpen(false);
  };

  return (
    <div className="flex justify-center">
      <TooltipProvider delayDuration={300}>
        <Popover open={open} onOpenChange={setOpen}>
          <div className="flex items-center rounded-lg border border-border bg-card h-11 transition-colors">
            {/* Older week */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={!canGoOlder}
                  onClick={() => onIndexChange(selectedIndex + 1)}
                  className="flex items-center justify-center w-9 h-full rounded-l-lg transition-colors text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous week"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Previous week</TooltipContent>
            </Tooltip>

            {/* Calendar trigger */}
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-center text-center font-normal bg-transparent border-0 text-foreground hover:bg-transparent px-3 h-full rounded-none min-w-[220px]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm">{formatWeekLabel(selectedWeek)}</span>
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent>Select week</TooltipContent>
            </Tooltip>

            {/* Newer week */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={!canGoNewer}
                  onClick={() => onIndexChange(selectedIndex - 1)}
                  className="flex items-center justify-center w-9 h-full rounded-r-lg transition-colors text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next week"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Next week</TooltipContent>
            </Tooltip>
          </div>

          <PopoverContent className="w-auto py-0 px-4" align="center">
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={handleWeekSelect}
              onDayMouseEnter={(date) => setHoveredDay(date)}
              onDayMouseLeave={() => setHoveredDay(null)}
              modifiers={{ hoveredWeek: hoveredWeekDays, today: [] as Date[] }}
              modifiersClassNames={{ hoveredWeek: "!bg-accent/50" }}
              numberOfMonths={1}
              weekStartsOn={1}
              defaultMonth={new Date()}
              toDate={new Date()}
              className="!p-0 [--cell-size:36px] [--primary:var(--brand-900)] [--ring:transparent] [--accent:color-mix(in_oklch,var(--brand-900)_15%,transparent)]"
            />
          </PopoverContent>
        </Popover>
      </TooltipProvider>
    </div>
  );
}
